'use client';

import { classNames } from '@lib/classNames/classNames';
import { cn } from '@lib/utils/cn';
import { useEffect, useRef, useState } from 'react';

import { LanguageSelectorProps } from './types';

import IconChevron from '@/assets/icons/iconChevron.svg';
import IconLanguage from '@/assets/icons/iconLanguage.svg';
import { LOCALES } from '@/shared/constants/common';
import { Locale } from '@/shared/types';

const isPageTranslated = (path: string, language: Locale) => {
  /** Ситискан и ГеоПоток переведены на казахский */
  if (/\/(cityscan|geoflow)/.test(path)) {
    return language === LOCALES.ru || language === LOCALES.kz;
  }

  return !(/\/(cases|partners|indoor)/.test(path) && language !== LOCALES.ru);
};

export const LanguageSelector = ({
  currentLanguage,
  variant = 'default',
  className,
  customClassName,
}: LanguageSelectorProps) => {
  const [isClicked, setIsClicked] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const selectorRef = useRef<HTMLDivElement>(null);
  const [selectedLanguage, setSelectedLanguage] = useState(currentLanguage);
  const isOpen = isClicked || isHovered;

  useEffect(() => {
    const handleDocumentClick = (event: Event) => {
      if (!selectorRef.current?.contains(event.target as Node)) {
        setIsClicked(false);
      }
    };
    if (isClicked) {
      document.addEventListener('click', handleDocumentClick);
    }

    return () => document.removeEventListener('click', handleDocumentClick);
  }, [isClicked]);

  const handleLanguageChange = (language: Locale) => {
    setSelectedLanguage(() => language);
    document.cookie = `NEXT_LOCALE=${language}; path=/;`;
    const pathWithoutLocale = window.location.pathname
      .replace(new RegExp(`^/${currentLanguage}(/|$)`), '')
      .replace(/^\/+/, ''); // удаляем лишние /
    const newHref = `/${language}/${pathWithoutLocale}${window.location.search}`;

    window.location.href = isPageTranslated(window.location.pathname, language)
      ? newHref
      : '/';
  };

  return (
    <div
      ref={selectorRef}
      className={cn('relative', variant === 'default' && 'w-24', className)}
      onMouseLeave={() => setIsHovered(false)}
      onMouseEnter={() => setIsHovered(true)}
    >
      <button
        className={cn('flex w-full items-center justify-center py-2')}
        onClick={() => setIsClicked(true)}
        aria-label={currentLanguage}
      >
        <IconLanguage />
        {currentLanguage.toUpperCase()}
        <IconChevron
          className={classNames({ mods: { 'rotate-180': isOpen } })}
        />
      </button>
      {isOpen && (
        <ul
          className={classNames({
            cls: 'rounded-20 bg-surface-base-blur absolute z-[101] w-full border-[1px] border-[#ffffff2e] backdrop-blur-[2px]',
            additional: [customClassName?.contentWrapper],
          })}
        >
          {Object.values(LOCALES).map((language) => (
            <li
              className={classNames({
                cls: 'm-2 cursor-pointer text-center',
                additional: [customClassName?.contentItem],
              })}
              key={language}
              onClick={() => handleLanguageChange(language)}
            >
              {language.toUpperCase()}

              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="ml-1 inline-block"
              >
                <circle
                  cx="10"
                  cy="10"
                  r="8"
                  stroke={
                    (language === currentLanguage &&
                      language === selectedLanguage) ||
                    language === selectedLanguage
                      ? 'var(--custom-base-inverse-primary)'
                      : '#ffffff2e'
                  }
                  strokeWidth="2"
                  fill="none"
                />
                {((language === currentLanguage &&
                  language === selectedLanguage) ||
                  language === selectedLanguage) && (
                  <circle
                    cx="10"
                    cy="10"
                    r="4"
                    fill="var(--custom-base-inverse-primary)"
                  />
                )}
              </svg>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
